import { Field, FormikHelpers, FormikProps } from "formik";
import { find } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SuggestionDataItem } from "react-mentions";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useAuthentication } from "../../context/authentication";
import { useComments } from "../../context/comments";
import { useMembers } from "../../context/members";
import MemberAvatarImg from "../../context/members/MemberAvatarImg";
import classNames from "../../utils/classNames";
import { Comment, CommentType } from "../../utils/types";
import CenteredLoading from "../CenteredLoading";
import FormProvider from "../forms/FormProvider";
import Mentions from "../forms/Mentions";
import CommentComponent from "./comment";

export interface Props {
  type: CommentType;
  typeUuid: string;
}

const CommentsComponent: React.FC<Props> = ({ type, typeUuid }) => {
  const { fetchComments, addComment, updateComment, removeComment, comments } =
    useComments();
  const { members } = useMembers();
  const { hash } = useLocation();
  const { user } = useAuthentication();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentComment, setCurrentComment] = useState<Comment | null>(null);
  const [currentDelete, setCurrentDelete] = useState<Comment | null>(null);
  const [fetchStatus, setFetchStatus] = useState<
    "idle" | "fetching" | "fetched"
  >("idle");

  const initialValues = useMemo(
    () => ({
      message: currentComment?.message || ""
    }),
    [currentComment]
  );

  useEffect(() => {
    if (!hash) return;

    const commentContainer = document.getElementById(hash);
    const defaultPageContainer = document.getElementById(
      "default-page-container"
    );

    if (!commentContainer || !defaultPageContainer) return;

    defaultPageContainer.scrollTo({
      top: commentContainer.offsetTop,
      behavior: "smooth"
    });
  }, [hash, comments]);

  const onSubmit = useCallback(
    async (
      formValues: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      setErrorMessage(null);

      try {
        let apiResponse;

        if (!currentComment) {
          apiResponse = await addComment(formValues.message, type, typeUuid);
        } else {
          apiResponse = await updateComment(
            formValues.message,
            type,
            typeUuid,
            currentComment.uuid
          );
        }

        if (apiResponse.status) {
          formikHelpers.resetForm();
          setCurrentComment(null);
        } else {
          setErrorMessage("Something went wrong");
        }

        formikHelpers.setSubmitting(false);
      } catch (error: any) {
        setErrorMessage(error?.message);
        formikHelpers.setSubmitting(false);
      }
    },
    [addComment, currentComment, type, typeUuid, updateComment]
  );

  const fetchCommentsList = useCallback(async () => {
    setFetchStatus("fetching");

    await fetchComments(type, typeUuid);

    setFetchStatus("fetched");
  }, [fetchComments, type, typeUuid]);

  useEffect(() => {
    if (fetchStatus === "idle") {
      fetchCommentsList();
    }
  }, [fetchCommentsList, fetchStatus]);

  const deleteComment = useCallback(
    async (comment: Comment) => {
      setCurrentDelete(comment);

      try {
        await removeComment(comment.uuid, type, typeUuid);

        if (comment.uuid === currentComment?.uuid) {
          setCurrentComment(null);
        }

        setCurrentDelete(null);
      } catch {
        setCurrentDelete(null);
      }
    },
    [currentComment?.uuid, removeComment, type, typeUuid]
  );

  const getUserSuggestions = useCallback(
    () =>
      members
        .filter(({ pending, deleted_at }) => !pending && !deleted_at)
        .map(({ uuid, name, email }) => ({
          id: uuid,
          display: name || email
        })),
    [members]
  );

  const renderUserSuggestion = (
    _suggestion: SuggestionDataItem,
    _search: string,
    highlightedDisplay: any
  ) => {
    return <p className="block text-sm">{highlightedDisplay}</p>;
  };

  const member = useMemo(
    () => find(members, { uuid: user.uuid }),
    [members, user.uuid]
  );

  if (fetchStatus === "fetching") {
    return <CenteredLoading className="mt-6" style={{ height: "134px" }} />;
  }

  return (
    <section>
      <ul className="space-y-6">
        {(comments[typeUuid] ?? []).map((comment: Comment) => (
          <CommentComponent
            onDelete={() => deleteComment(comment)}
            loadingDelete={comment.uuid === currentDelete?.uuid}
            onEdit={() => setCurrentComment(comment)}
            comment={comment}
            className={classNames(
              "first:mt-6",
              currentComment?.uuid === comment.uuid && "border rounded p-2"
            )}
            key={comment.uuid}
          />
        ))}
      </ul>

      <div className="flex space-x-3 mt-6">
        <div className="flex-shrink-0">
          <MemberAvatarImg member={member} sizeInPx={40} />
        </div>
        <div className="w-full">
          <FormProvider
            error={errorMessage}
            onCancel={() => setCurrentComment(null)}
            submitButtonText={currentComment ? "Update" : "Comment"}
            submittingSubmitButtonText={currentComment ? "Updating" : "Sending"}
            classnames={{
              wrapper: "sm:max-w-none",
              buttonWrapper: "pr-0 pb-0",
              cancelButton: currentComment ? "block" : "hidden"
            }}
            formikProps={{
              onSubmit,
              initialValues,
              validationSchema: Yup.object().shape({
                message: Yup.string().required("Required")
              })
            }}
            orderedFieldNames={ORDERED_FIELD_NAMES}
          >
            {({ isSubmitting }: FormikProps<FormValues>) => {
              return (
                <Field
                  id="message"
                  name="message"
                  placeholder="Write a comment"
                  component={Mentions}
                  withoutErrorLabel
                  data={getUserSuggestions}
                  renderSuggestion={renderUserSuggestion}
                  disabled={isSubmitting}
                />
              );
            }}
          </FormProvider>
        </div>
      </div>
    </section>
  );
};

export default CommentsComponent;

//
// Utils
//

interface FormValues {
  message: string;
}

const ORDERED_FIELD_NAMES = ["message"];
